import React, { useState, useEffect } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import api from '../api';
import { toast } from 'sonner';
import { carrierLogoUrl } from '../utils/carrierLogos';

function ProductSettingsModal({ product, company, onClose, onSave }) {
  const [settings, setSettings] = useState({
    // Preserve any pre-existing fields untouched — the simplified modal only
    // edits the three fields below, but we don't want to nuke legacy data.
    ...(product?.settings || {}),
    jumpstart_form: product?.settings?.jumpstart_form || 'default',
    app_url:        product?.settings?.app_url        || '',
    jumpstart_url:  product?.settings?.jumpstart_url  || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/products/update-product', {
        company_id: company.id,
        product_id: product.id,
        field: 'settings',
        value: settings,
      });
      onSave(settings);
      onClose();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const Field = ({ label, hint, children }) => (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
        <label style={{ fontWeight: '600', fontSize: '14px', color: '#0F172A' }}>{label}</label>
        {hint && (
          <div style={{ position: 'relative', display: 'inline-flex' }} title={hint}>
            <i className="fas fa-circle-info" style={{ color: '#94A3B8', fontSize: '13px', cursor: 'help' }}></i>
          </div>
        )}
      </div>
      {children}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(2px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #DDE7F1', width: '100%', maxWidth: '560px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(15,23,42,0.15)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #DDE7F1' }}>
          <div>
            <div style={{ fontWeight: '700', fontSize: '16px', color: '#0F172A' }}>Customize Product — {product.name}</div>
            <div style={{ fontSize: '12px', color: '#64748B', marginTop: '1px' }}>{company.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '18px' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>
          <Field label="Jumpstart Form" hint="Choose which Jumpstart form is offered to the customer for this product.">
            <select className="input-field" value={settings.jumpstart_form} onChange={e => set('jumpstart_form', e.target.value)} style={{ appearance: 'none', cursor: 'pointer' }}>
              <option value="default">Default</option>
              <option value="short_form">Jumpstart Short Form</option>
            </select>
          </Field>

          <Field label="Custom Apply Page URL" hint="Override where users go after clicking 'Apply'. Leave blank for default.">
            <input className="input-field" type="url" value={settings.app_url} onChange={e => set('app_url', e.target.value)} placeholder="https://yoursite.com/apply" />
          </Field>

          <Field label="Custom Jumpstart URL" hint="Override where users go after clicking 'Continue' on the Jumpstart card. Leave blank for default.">
            <input className="input-field" type="url" value={settings.jumpstart_url} onChange={e => set('jumpstart_url', e.target.value)} placeholder="https://yoursite.com/jumpstart" />
          </Field>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #DDE7F1', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button className="btn-secondary" style={{ height: '38px', padding: '0 18px', fontSize: '14px' }} onClick={onClose}>Cancel</button>
          <button className="btn-primary" style={{ height: '38px', padding: '0 18px', fontSize: '14px' }} onClick={handleSave} disabled={saving}>
            {saving ? <><i className="fas fa-spinner fa-spin mr-1"></i>Saving...</> : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ active, onChange }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onChange(); }}
      style={{
        width: '44px', height: '24px', borderRadius: '999px',
        background: active ? '#EFF6FF' : '#F1F5F9',
        border: `1px solid ${active ? '#2D87F0' : '#DDE7F1'}`,
        position: 'relative', cursor: 'pointer', flexShrink: 0,
        padding: 0, overflow: 'hidden', transition: 'all 200ms',
      }}
    >
      <span style={{
        position: 'absolute', top: '50%',
        left: active ? 'calc(100% - 19px)' : '3px',
        transform: 'translateY(-50%)',
        width: '16px', height: '16px', borderRadius: '50%',
        background: active ? '#2D87F0' : '#94A3B8',
        transition: 'left 200ms, background 200ms',
      }} />
    </button>
  );
}

function CompanyCard({ company, expanded, onToggleExpand, onToggleCompany, onToggleProduct, onRecommendProduct, onEditProduct }) {
  const localLogo = carrierLogoUrl(company.name);
  const clearbitLogo = company.logo_domain ? `https://logo.clearbit.com/${company.logo_domain}` : null;
  const logoUrl = localLogo || clearbitLogo;
  const [logoError, setLogoError] = useState(false);

  const totalProducts = (company.products || []).length;
  const activeProducts = (company.products || []).filter(p => p.active).length;
  const allActive = activeProducts === totalProducts;
  const noneActive = activeProducts === 0;

  // Badge color
  const badgeColor = noneActive ? '#94A3B8' : allActive ? '#16A34A' : '#F59E0B';
  const badgeBg    = noneActive ? '#F1F5F9'  : allActive ? '#F0FDF4'  : '#FFFBEB';

  return (
    <div className="portal-card" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Company header — logo row, then name row */}
      <div style={{ padding: '12px 14px', borderBottom: expanded ? '1px solid #DDE7F1' : 'none', background: 'white' }}>
        {/* Row 1: chevron + logo (left-aligned) + badge + toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <button
            onClick={onToggleExpand}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: '2px', flexShrink: 0, fontSize: '12px', transition: 'transform 150ms', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', marginRight: '12px' }}
          >
            <i className="fas fa-chevron-right"></i>
          </button>

          {/* Carrier logo (left-aligned) */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', minHeight: '32px', minWidth: 0 }}>
            {logoUrl && !logoError ? (
              <img src={logoUrl} alt={company.name} onError={() => setLogoError(true)}
                style={{ maxWidth: '150px', maxHeight: '32px', objectFit: 'contain' }} />
            ) : (
              <i className="fas fa-building" style={{ color: '#CBD5E1', fontSize: '22px' }}></i>
            )}
          </div>

          <span style={{ fontSize: '11px', fontWeight: '600', color: badgeColor, background: badgeBg, borderRadius: '999px', padding: '2px 8px', flexShrink: 0, border: `1px solid ${badgeBg === '#F1F5F9' ? '#DDE7F1' : badgeBg}`, whiteSpace: 'nowrap' }}>
            {activeProducts}/{totalProducts}
          </span>

          <Toggle active={company.active} onChange={() => onToggleCompany(company)} />
        </div>

        {/* Row 2: company name (left-aligned, indented past chevron) */}
        <div onClick={onToggleExpand} style={{ marginTop: '8px', marginLeft: '34px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#0F172A', lineHeight: '1.3', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {company.name}
        </div>
      </div>

      {/* Expanded content: products only — logo lives in the header now */}
      {expanded && (
        <div style={{ padding: '4px 14px 12px' }}>
          {(company.products || []).map(product => (
              <div key={product.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 0', borderBottom: '1px solid #F8FAFC' }}>
                <button onClick={() => onRecommendProduct(company, product)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px', flexShrink: 0 }}>
                  <i className={`${product.recommended ? 'fas' : 'far'} fa-star`}
                    style={{ color: product.recommended ? '#F59E0B' : '#CBD5E1', fontSize: '13px' }}></i>
                </button>
                <span style={{ flex: 1, fontSize: '12px', color: product.active ? '#334155' : '#94A3B8', lineHeight: '1.3' }}>
                  {product.name}
                </span>
                {/* Edit link */}
                <button
                  onClick={() => onEditProduct(company, product)}
                  title="Configure product settings"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '11px', padding: '1px 4px', flexShrink: 0, fontWeight: '500' }}
                  onMouseEnter={e => e.target.style.color = '#2D87F0'}
                  onMouseLeave={e => e.target.style.color = '#94A3B8'}
                >
                  <i className="fas fa-pen-to-square mr-1"></i>Edit
                </button>
                <Toggle active={product.active} onChange={() => onToggleProduct(company, product)} />
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default function ProductsPage() {
  const [companies, setCompanies] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [editingProduct, setEditingProduct] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => !!localStorage.getItem('qp_help_dismissed_products')
  ); // { product, company }

  useEffect(() => { fetchCompanies(); }, []);

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/products');
      const cos = res.data.companies || [];
      setCompanies(cos);
      // Expand all cards by default
      setExpandedIds(new Set(cos.map(c => c.id)));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateCompany = (companyId, updater) =>
    setCompanies(prev => prev.map(c => c.id === companyId ? updater(c) : c));

  // 7. Toast feedback
  const handleToggleCompany = async (company) => {
    const newActive = !company.active;
    updateCompany(company.id, c => ({ ...c, active: newActive }));
    toast.success(`${company.name} ${newActive ? 'enabled' : 'disabled'}`);
    try { await api.post('/products/update-company', { company_id: company.id, field: 'active', value: newActive }); }
    catch (e) { /* silent */ }
  };

  const handleToggleProduct = async (company, product) => {
    const newActive = !product.active;
    updateCompany(company.id, c => ({
      ...c, products: c.products.map(p => p.id === product.id ? { ...p, active: newActive } : p),
    }));
    toast.success(`${product.name} ${newActive ? 'enabled' : 'disabled'}`);
    try { await api.post('/products/update-product', { company_id: company.id, product_id: product.id, field: 'active', value: newActive }); }
    catch (e) { /* silent */ }
  };

  const handleRecommendProduct = async (company, product) => {
    const newRec = !product.recommended;
    updateCompany(company.id, c => ({
      ...c, products: c.products.map(p => p.id === product.id ? { ...p, recommended: newRec } : p),
    }));
    toast.success(newRec ? `${product.name} marked as recommended` : `${product.name} recommendation removed`);
    try { await api.post('/products/update-product', { company_id: company.id, product_id: product.id, field: 'recommended', value: newRec }); }
    catch (e) { /* silent */ }
  };

  const toggleExpand = (id) =>
    setExpandedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const expandAll  = () => setExpandedIds(new Set(filtered.map(c => c.id)));
  const collapseAll = () => setExpandedIds(new Set());

  const filtered = companies.filter(c => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return c.name.toLowerCase().includes(q) ||
      (c.products || []).some(p => p.name.toLowerCase().includes(q));
  });

  // 2. Summary stats
  const totalCompanies  = companies.length;
  const activeCompanies = companies.filter(c => c.active).length;
  const totalProducts   = companies.reduce((sum, c) => sum + (c.products?.length || 0), 0);
  const activeProducts  = companies.reduce((sum, c) => sum + (c.products?.filter(p => p.active).length || 0), 0);
  const totalRecommended = companies.reduce((sum, c) => sum + (c.products?.filter(p => p.recommended).length || 0), 0);

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>

        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="fas fa-suitcase" style={{ color: '#2D87F0', fontSize: '18px' }}></i>
            </div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Companies &amp; Products</h1>
          </div>

          {/* How-to banner */}
          {!bannerDismissed && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '16px', padding: '14px 18px', background: 'white', border: '1px solid #DDE7F1', borderLeft: '4px solid #2D87F0', borderRadius: '10px', boxShadow: '0 1px 4px rgba(15,23,42,0.06)', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1, minWidth: '200px' }}>
                <i className="fas fa-graduation-cap" style={{ color: '#2D87F0', fontSize: '20px', marginTop: '2px', flexShrink: 0 }}></i>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '14px', color: '#0F172A', marginBottom: '2px' }}>Setup Guide: Companies &amp; Products</div>
                  <div style={{ fontSize: '13px', color: '#64748B' }}>Video walkthrough and step-by-step documentation available.</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <button onClick={() => setShowVideo(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: 'white', background: showVideo ? '#1D6FD8' : '#2D87F0', border: 'none', cursor: 'pointer', padding: '7px 14px', borderRadius: '7px', whiteSpace: 'nowrap' }}>
                  <i className={`fas ${showVideo ? 'fa-times' : 'fa-play'}`} style={{ fontSize: '11px' }}></i>
                  {showVideo ? 'Close Video' : 'Watch Video'}
                </button>
                <a href="https://app.modalsupport.com/article/details/CMf8PDYWLmrdhpTepL5E/NH92DY13mlejc3It6jyK?preview=true" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#2D87F0', background: '#EFF6FF', border: '1px solid #BFDBFE', textDecoration: 'none', padding: '7px 14px', borderRadius: '7px', whiteSpace: 'nowrap' }}>
                  <i className="fas fa-file-lines" style={{ fontSize: '11px' }}></i> Read Article
                </a>
                <button onClick={() => { localStorage.setItem('qp_help_dismissed_products', '1'); setBannerDismissed(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px', padding: '4px', lineHeight: 1 }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          )}

          {/* Inline video */}
          {showVideo && (
            <div className="portal-card" style={{ overflow: 'hidden', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #DDE7F1', background: '#F8FAFC' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-play-circle" style={{ color: '#2D87F0', fontSize: '16px' }}></i>
                  <span style={{ fontWeight: '600', fontSize: '14px', color: '#0F172A' }}>Setup Guide: Companies &amp; Products</span>
                </div>
                <button onClick={() => setShowVideo(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px' }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                <iframe
                  src="https://embed.app.guidde.com/playbooks/6g1v2QkhKHjCqf4dPZ8dUY?mode=videoOnly"
                  title="Manage Insurance Companies And Products In Quoteplicity"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: '10px' }}
                  referrerPolicy="unsafe-url"
                  allow="clipboard-write"
                  sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {/* Unified toolbar: stats + search + expand controls */}
          {!loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '10px 16px', background: 'white', border: '1px solid #DDE7F1', borderRadius: '10px', boxShadow: '0 1px 3px rgba(15,23,42,0.05)' }}>

              {/* Stats */}
              {[
                { icon: 'fas fa-building',  value: `${activeCompanies}/${totalCompanies}`,  label: 'companies',  color: '#2D87F0' },
                { icon: 'fas fa-toggle-on', value: `${activeProducts}/${totalProducts}`,    label: 'products',   color: '#16A34A' },
                { icon: 'fas fa-star',       value: totalRecommended,                        label: 'recommended', color: '#D97706' },
              ].map((stat, i) => (
                <React.Fragment key={stat.label}>
                  {i > 0 && <div style={{ width: '1px', height: '24px', background: '#DDE7F1', flexShrink: 0 }} />}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                    <i className={stat.icon} style={{ color: stat.color, fontSize: '14px' }}></i>
                    <span style={{ fontWeight: '700', fontSize: '14px', color: stat.color }}>{stat.value}</span>
                    <span style={{ fontSize: '12px', color: '#64748B' }}>{stat.label}</span>
                  </div>
                </React.Fragment>
              ))}

              {/* Divider */}
              <div style={{ flex: 1 }} />

              {/* Search */}
              <div style={{ position: 'relative' }}>
                <i className="fas fa-search" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '12px', pointerEvents: 'none' }}></i>
                <input type="text" className="input-field"
                  style={{ width: '220px', paddingLeft: '30px', height: '34px', fontSize: '13px' }}
                  placeholder="Start typing to filter..."
                  value={searchText} onChange={e => setSearchText(e.target.value)} />
              </div>

              {/* Expand / Collapse */}
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={expandAll} className="btn-secondary" style={{ height: '34px', padding: '0 12px', fontSize: '12px' }}>
                  <i className="fas fa-expand mr-1"></i>Expand All
                </button>
                <button onClick={collapseAll} className="btn-secondary" style={{ height: '34px', padding: '0 12px', fontSize: '12px' }}>
                  <i className="fas fa-compress mr-1"></i>Collapse All
                </button>
              </div>

            </div>
          )}
        </div>

        {/* Company grid */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {[...Array(9)].map((_, i) => (
              <div key={i} className="skeleton" style={{ height: '80px', borderRadius: '12px' }}></div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="portal-card" style={{ padding: '40px', textAlign: 'center', color: '#94A3B8' }}>
            <i className="fas fa-search" style={{ fontSize: '32px', marginBottom: '12px', display: 'block' }}></i>
            No companies match your search.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {filtered.map(company => (
              <CompanyCard
                key={company.id}
                company={company}
                expanded={expandedIds.has(company.id)}
                onToggleExpand={() => toggleExpand(company.id)}
                onToggleCompany={handleToggleCompany}
                onToggleProduct={handleToggleProduct}
                onRecommendProduct={handleRecommendProduct}
                onEditProduct={(company, product) => setEditingProduct({ company, product })}
              />
            ))}
          </div>
        )}
      </div>

      {/* Product settings modal */}
      {editingProduct && (
        <ProductSettingsModal
          product={editingProduct.product}
          company={editingProduct.company}
          onClose={() => setEditingProduct(null)}
          onSave={(settings) => {
            setCompanies(prev => prev.map(c =>
              c.id === editingProduct.company.id
                ? { ...c, products: c.products.map(p => p.id === editingProduct.product.id ? { ...p, settings } : p) }
                : c
            ));
          }}
        />
      )}
    </ProtectedLayout>
  );
}
